import React, { useCallback, useEffect, useState } from 'react';
import { Cpu, Loader2, RefreshCw } from 'lucide-react';
import Card from '../../components/Card';
import { getAiModels } from './driveSettingsApi';

const FEATURE_LABELS = {
  contractExtraction: 'Contract extraction',
  keyTerms: 'Key terms and obligations',
  jsonRepair: 'JSON repair',
  ndaReview: 'NDA review',
  ndaClassifier: 'NDA classification',
  tprmReview: 'TPRM review',
  tprmPrefill: 'TPRM form prefill',
  generationFields: 'Contract generation fields',
  router: 'Slack and contract assistant',
  structuredData: 'Structured data (default)',
  schemaGen: 'Schema drafting',
};

function Source({ value }) {
  return value === 'code' ? 'Code default' : <code className="mono break-all">{value}</code>;
}

export default function AiModelsPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setConfig(null);
    try {
      setConfig(await getAiModels());
    } catch (err) {
      setError(err.message || 'Could not load AI model configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Card
      title="AI models"
      icon={Cpu}
      action={(
        <button type="button" className="btn outline sm" onClick={refresh} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5${loading ? ' animate-spin' : ''}`} />
          Refresh
        </button>
      )}
    >
      <p className="muted text-sm mb-4">
        Current model settings reported by the running backend. Environment overrides take
        priority over the code default; feature overrides take priority over the app-wide override.
      </p>

      {loading && (
        <div role="status" className="flex items-center gap-2 py-4 muted text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading model configuration…
        </div>
      )}
      {error && <div role="alert" className="callout red">{error}</div>}

      {config && (
        <div className="space-y-6">
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="muted mb-1">Code default</dt>
              <dd className="mono break-all">{config.codeDefault}</dd>
            </div>
            <div>
              <dt className="muted mb-1">Effective app-wide default</dt>
              <dd className="mono break-all">{config.default.model}</dd>
              <dd className="muted text-xs mt-1">Source: <Source value={config.default.source} /></dd>
            </div>
            <div>
              <dt className="muted mb-1">Refusal retry model</dt>
              <dd className="mono break-all">{config.refusalFallback}</dd>
            </div>
          </dl>

          <section aria-labelledby="model-overrides-title">
            <h3 id="model-overrides-title" className="font-medium text-sm mb-2">Environment overrides</h3>
            <div className="overflow-x-auto">
              <table className="tbl" aria-label="Model environment overrides">
                <thead><tr><th scope="col">Variable</th><th scope="col">Status</th><th scope="col">Value</th></tr></thead>
                <tbody>
                  {config.overrides.map(({ name, isSet, value }) => (
                    <tr key={name}>
                      <td><code className="mono break-all">{name}</code></td>
                      <td>{!isSet ? 'Not set' : value.trim() ? 'Set' : 'Set (blank; ignored)'}</td>
                      <td><code className="mono break-all">{isSet ? JSON.stringify(value) : '—'}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section aria-labelledby="effective-models-title">
            <h3 id="effective-models-title" className="font-medium text-sm mb-2">Effective model by feature</h3>
            <div className="overflow-x-auto">
              <table className="tbl" aria-label="Effective models by feature">
                <thead><tr><th scope="col">Feature</th><th scope="col">Model</th><th scope="col">Source</th></tr></thead>
                <tbody>
                  {config.surfaces.map(({ surface, model, source, resolvedAt }) => (
                    <tr key={surface}>
                      <td>
                        {FEATURE_LABELS[surface] || surface}
                        {resolvedAt === 'startup' && <div className="muted text-xs">Selected at startup</div>}
                      </td>
                      <td><code className="mono break-all">{model}</code></td>
                      <td><Source value={source} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted text-xs mt-3">
              Schemas pinned to a specific model keep that selection. A refused request may retry
              on the refusal retry model shown above. The assistant keeps its startup selection
              until the backend restarts.
            </p>
          </section>
        </div>
      )}
    </Card>
  );
}
